/**
    * NYB Geolocation - IP-based content visibility
    * Shows/hides blocks based on user's geographic location
    * Usage: Add attribute nyb-geo-filter="NY,NJ,CT" to show block only for those states
*/

const NYBGeolocation = (() => {

    const CONFIG = {
        apiKey: '307d996e407f405ead231606703ee67c',
        apiUrl: 'https://api.geoapify.com/v1/ipinfo',
        cacheKey: 'geotarget',
        stateKey: 'geotarget-state',
        cacheDuration: 1000 * 60 * 60 * 24 * 7, // 7 days
        delay: 500
    };

    // Map of state names to codes
    const STATE_CODES = {
        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
        'Washington D.C.': 'DC', 'District of Columbia': 'DC'
    };

    // States by zone
    const Z2_STATE_CODES = ['NY','MA','PA','RI','CT','DC','DE','MD'];
    const Z3456_STATE_CODES = ['AL','AK','AZ','AR','CA','CO','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NC','ND','OH','OK','OR','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

    let userStateCode = null;
    const getStateCode = (stateName) => STATE_CODES[stateName] || stateName;

    const parseStoredState = (stateString) => {
        if (!stateString) {
            return null;
        }
        const [state, city] = stateString.split('-');
        return { 
            state, 
            city 
        };
    };

    const applyGeoFilters = (stateCode) => {

        if (!stateCode) {
            return;
        } 

        document.querySelectorAll('[nyb-geo-filter]').forEach(el => {

            const allowedStates = el.getAttribute('nyb-geo-filter').split(',').map(s => s.trim().toUpperCase());

            // Add states by zone
            if (allowedStates.includes('Z2')) {
                allowedStates.push(...Z2_STATE_CODES);
            }
            
            if (allowedStates.includes('Z3456')) {
                allowedStates.push(...Z3456_STATE_CODES);
            }
            
            // Display or hide block
            if (allowedStates.includes(stateCode.toUpperCase())) {
                el.removeAttribute('nyb-geo-filter');
            } 
            else {
                el.style.display = 'none';
            }
        });
    };

    const processLocationResult = (result) => {

        const stateName = result.state?.name || '';
        const cityName = result.city?.name || '';
        const countryCode = result.country?.iso_code || '';

        // Cache the state info
        localStorage.setItem(CONFIG.stateKey, `${stateName}-${cityName}`);

        // Non-US users default to Z3456
        if (countryCode !== 'US') {
            userStateCode = 'INTL';
            localStorage.setItem(CONFIG.stateKey, 'INTL-');
            applyGeoFilters('Z3456');
            return;
        }

        userStateCode = getStateCode(stateName);
        applyGeoFilters(userStateCode);
    };

    const fetchLocation = async () => {
        try {
            
            const response = await fetch(`${CONFIG.apiUrl}?apiKey=${CONFIG.apiKey}`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const result = await response.json();
            processLocationResult(result);
        } 
        catch (error) {
            console.warn('NYBGeolocation: Failed to fetch location', error);
            applyGeoFilters('Z3456');
        }
    };

    const isCacheExpired = () => {
        
        const cached = localStorage.getItem(CONFIG.cacheKey);
        
        if (!cached || cached === 'test') {
            return true;
        }
        return (Date.now() - parseInt(cached, 10)) > CONFIG.cacheDuration;
    };

    const init = () => {

        const cachedState = localStorage.getItem(CONFIG.stateKey);

        // Apply cached state immediately if available
        if (cachedState) {
            
            const parsed = parseStoredState(cachedState);

            if (parsed?.state) {
                if (parsed.state === 'INTL') {
                    userStateCode = 'INTL';
                    applyGeoFilters('Z3456');
                } 
                else {
                    userStateCode = getStateCode(parsed.state);
                    applyGeoFilters(userStateCode);
                }
            }
        }

        // Fetch fresh data if cache expired
        setTimeout(() => {
            if (isCacheExpired()) {
                localStorage.setItem(CONFIG.cacheKey, Date.now().toString());
                fetchLocation();
            }
        }, CONFIG.delay);
    };

    return { 
        init, 
        applyGeoFilters, 
        getStateCode: () => userStateCode 
    };
})();

NYBGeolocation.init();
